import { onDomReady } from '../utils/dom';

/*
 * Scroll-Einblendungen: vergibt .animate-in an die Sektions-Elemente und an die Karten in
 * Karten-Gruppen, sobald sie in den Viewport treten — das Styling liegt in
 * scss/library/_animation.scss. Elemente, die gemeinsam eintreten (Karten-Reihen), werden über
 * --animate-in-delay gestaffelt.
 * Tastatur/Screenreader: focusin blendet das Ziel-Element sofort und ohne Delay dauerhaft ein —
 * ein unsichtbares, aber fokussierbares Element darf es nie geben.
 */

const ITEM_SELECTOR = '#main .mod_article .content-wrapper > *:not(script)';
const CARD_GROUP_SELECTOR = '#main .mod_article .content-wrapper > :is(.content-element-group, .content-distributor-list).grid';
const CHILD_ITEM_SELECTOR = '#main .mod_article .animate-in-children > *:not(script)';
const STAGGER_MS = 90;
const STAGGER_MAX_MS = 450;

/*
 * Karten-Gruppen (Vorteile, Ursachen, Händler): jede Karte steigt einzeln gestaffelt ein statt
 * der Gruppe als ein Block. Die Gruppe wird dafür per no-animate-in aus dem Standard genommen,
 * ihr eigener Fade würde die Staffelung sonst überdecken.
 * Nur direkte Kinder eines content-wrapper: Gruppen in eingeklappten Akkordeons wären für den
 * IntersectionObserver nie sichtbar und blieben unsichtbar stehen.
 */
function markCardGroups() {
    for (const group of document.querySelectorAll(CARD_GROUP_SELECTOR)) {
        group.classList.add('animate-in-children', 'no-animate-in');
    }
}

function addAnimateInClasses() {
    markCardGroups();

    const items = document.querySelectorAll(`${ITEM_SELECTOR}:not(.no-animate-in), ${CHILD_ITEM_SELECTOR}`);

    if (!items.length) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            let batchIndex = 0;

            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }

                reveal(entry.target, Math.min(batchIndex * STAGGER_MS, STAGGER_MAX_MS));
                batchIndex += 1;
            }
        },
        { rootMargin: '0px 0px -200px 0px', threshold: 0 },
    );

    function reveal(item, delay = 0) {
        item.style.setProperty('--animate-in-delay', `${delay}ms`);
        item.classList.add('animate-in');
        observer.unobserve(item); // fire once
    }

    // Beim Init (teilweise) sichtbare Elemente sofort freigeben statt beobachten: Ihr
    // versteckter Ausgangszustand greift erst mit html.loaded (~0,4s nach dem Loader-Fade) —
    // sie würden sonst sichtbar → versteckt springen (Flackern) und erst beim Scrollen wiederkommen.
    // Erst ALLE Rects lesen, dann schreiben — abwechselndes Lesen/Schreiben erzwingt pro Element
    // ein synchrones Layout (Forced Reflow, ruckelt beim Laden).
    const rects = [...items].map((item) => item.getBoundingClientRect());

    items.forEach((item, i) => {
        if (rects[i].top < window.innerHeight && rects[i].bottom > 0) {
            reveal(item);
        } else {
            observer.observe(item);
        }
    });

    document.addEventListener('focusin', (event) => {
        const item = event.target.closest(`${ITEM_SELECTOR}, ${CHILD_ITEM_SELECTOR}`);

        if (item && !item.classList.contains('animate-in')) {
            reveal(item);
        }
    });
}

onDomReady(() => {
    addAnimateInClasses();
});
